import { useState, useEffect } from 'react';
import DocuSignMappingModal from 'modules/integrations/docuSign/ui/modals/DocuSignMappingModal';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { FormSelect } from 'components/form';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import {
  Dialog,
  Stack,
  Button,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Modal,
} from '@mui/material';
import { docuSignApi } from '../../api';
import { TemplateOptionsResponse, TemplateOptionsVariables } from '../../api/types';
import { DOCU_SIGN_TAB_MAPPINGS } from '../../api/queries';
import { useNotifications } from 'modules/notification';
import { logError } from 'utils/logger';

export const DocuSignDialog = () => {
  const history = useHistory();
  const { data, isOpen, handleClose } = useModal(ModalsKeys.DocuSignDialog);
  const { showNotification } = useNotifications();

  const [selectedTemplate, setSelectedTemplate] = useState<{ templateId: string; docusignAccountId: string }>({
    templateId: '',
    docusignAccountId: '',
  });
  const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);

  const {
    loading: isDocusignTemplateListLoading,
    data: templatesOptionsResponse,
    error: templatesError,
  } = useQuery<TemplateOptionsResponse, TemplateOptionsVariables>(docuSignApi.getTemplates, {
    variables: {
      VOId: data.vendorOpportunityId,
    },
    skip: !isOpen || !data?.vendorOpportunityId,
  });

  useEffect(() => {
    if (templatesError) {
      logError(templatesError);
      showNotification('Failed to load DocuSign templates', { type: 'error' });
    }
  }, [templatesError, showNotification]);

  const handleGoToTemplate = () => {
    history.push({ pathname: `/docu/sign/${data.link}` });
  };

  // Open MappingModal instead of sending envelope directly and hide DocuSignDialog
  const handleSelectClick = () => {
    setIsMappingModalOpen(true);
  };

  const handleMappingModalClose = (reason?: 'cancel' | 'sent') => {
    setIsMappingModalOpen(false);
    if (reason === 'sent') {
      handleClose();
    }
  };

  return (
    <>
      <Dialog open={isOpen} fullWidth maxWidth="lg" onClose={handleClose}>
        <DialogTitle>Send via DocuSign</DialogTitle>

        <DialogContent dividers>
          <RenderWithFallback
            isLoading={isDocusignTemplateListLoading}
            fallBack={
              <Stack gap="0.5rem" alignItems="center" justifyContent="center">
                <Typography>Loading templates</Typography>
                <CircularProgress />
              </Stack>
            }
          >
            <FormSelect
              value={selectedTemplate.templateId}
              label="Templates for your Account"
              options={templatesOptionsResponse?.options ?? []}
              disabled={!templatesOptionsResponse?.options?.length}
              onChange={event => {
                const templateId = event.target.value as string;
                const selectedOption = templatesOptionsResponse?.options?.find(opt => opt.value === templateId);
                setSelectedTemplate({
                  templateId,
                  docusignAccountId: selectedOption?.docusignAccountId ?? '',
                });
              }}
            />

            <RenderWithCondition condition={!!data.link}>
              <Button onClick={handleGoToTemplate}>View Created Proposal</Button>
            </RenderWithCondition>
          </RenderWithFallback>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>

          <Button variant="contained" disabled={!selectedTemplate.templateId} onClick={handleSelectClick}>
            Select
          </Button>
        </DialogActions>
      </Dialog>

      {isMappingModalOpen && data && (
        <Modal open={isMappingModalOpen} onClose={() => handleMappingModalClose('cancel')}>
          <DocuSignMappingModal
            vendorOpportunityId={data.vendorOpportunityId}
            templateId={selectedTemplate.templateId}
            docusignAccountId={selectedTemplate.docusignAccountId}
            handleClose={handleMappingModalClose}
            query={DOCU_SIGN_TAB_MAPPINGS}
            variables={{
              input: {
                voId: data.vendorOpportunityId,
                templateId: selectedTemplate.templateId,
                creditSubmissionDynamicsId: data.creditSubmissionDynamicsId,
              },
            }}
            dataKey="docuSignTabMappings"
          />
        </Modal>
      )}
    </>
  );
};
